// frontend/src/components/TradeLogs.js
// Real-time trade log viewer — streams strategy logs from backend via Socket.IO

import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { socket } from "../App";

const MAX_LOGS = 150;

const LEVEL_STYLES = {
  info: { color: "#94a3b8", icon: "📋" },
  success: { color: "#22c55e", icon: "✅" },
  warning: { color: "#facc15", icon: "⚠️" },
  error: { color: "#ef4444", icon: "❌" },
  entry: { color: "#818cf8", icon: "🚀" },
  exit: { color: "#f97316", icon: "🔔" },
};

export default function TradeLogs() {
  const [logs, setLogs] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState("all");
  const containerRef = useRef(null);
  const bottomRef = useRef(null);

  // Fetch initial logs
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/debug/trade-logs`);
        if (data?.ok) setLogs(data.logs || []);
      } catch (err) {
        console.error("Failed to fetch trade logs:", err.message);
      }
    };
    fetchLogs();
  }, []);

  // Listen for real-time logs
  useEffect(() => {
    const handleLog = (entry) => {
      setLogs((prev) => {
        const next = [...prev, entry];
        return next.length > MAX_LOGS ? next.slice(-MAX_LOGS) : next;
      });
    };

    socket.on("trade_log", handleLog);
    return () => socket.off("trade_log", handleLog);
  }, []);

  // Auto-scroll within container only (not the whole page)
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Handle scroll to detect manual scroll-up
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 40;
    setAutoScroll(atBottom);
  }, []);

  const clearLogs = () => setLogs([]);

  const filteredLogs = filter === "all"
    ? logs
    : logs.filter((l) => l.level === filter);

  return (
    <div className="trade-logs-card">
      <div className="card-header">
        <div className="trade-logs-title-row">
          <span className="card-icon">📋</span>
          <h3>Trade Logs</h3>
          <span className="log-count">{filteredLogs.length}</span>
        </div>
        <div className="trade-logs-controls">
          <select
            id="trade-logs-filter"
            className="log-filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warnings</option>
            <option value="error">Errors</option>
            <option value="entry">Entries</option>
            <option value="exit">Exits</option>
          </select>
          <button
            id="trade-logs-clear-btn"
            className="log-clear-btn"
            onClick={clearLogs}
            title="Clear logs"
          >
            🗑️
          </button>
          <button
            className={`log-autoscroll-btn ${autoScroll ? "active" : ""}`}
            onClick={() => setAutoScroll(!autoScroll)}
            title={autoScroll ? "Auto-scroll ON" : "Auto-scroll OFF"}
          >
            {autoScroll ? "⬇️" : "⏸️"}
          </button>
        </div>
      </div>

      <div
        className="trade-logs-body"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {filteredLogs.length === 0 ? (
          <div className="trade-logs-empty">
            Waiting for strategy logs...
          </div>
        ) : (
          filteredLogs.map((log, i) => {
            const style = LEVEL_STYLES[log.level] || LEVEL_STYLES.info;
            return (
              <div key={i} className={`log-entry log-${log.level}`}>
                <span className="log-time">{log.time}</span>
                <span className="log-icon">{style.icon}</span>
                <span className="log-msg" style={{ color: style.color }}>
                  {log.message}
                </span>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <style>{`
        .trade-logs-card {
          background: var(--color-bg-card);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          margin-bottom: 24px;
          overflow: hidden;
        }

        .trade-logs-card .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          border-bottom: 1px solid var(--color-border);
          background: rgba(255, 255, 255, 0.02);
        }

        .trade-logs-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .trade-logs-title-row h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .log-count {
          background: rgba(99, 102, 241, 0.2);
          color: #818cf8;
          padding: 2px 8px;
          border-radius: var(--radius-full);
          font-size: 0.72rem;
          font-weight: 600;
          font-family: var(--font-family-mono);
        }

        .trade-logs-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .log-filter-select {
          padding: 5px 10px;
          font-size: 0.78rem;
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          color: var(--color-text-secondary);
          outline: none;
          cursor: pointer;
        }

        .log-filter-select:focus {
          border-color: var(--color-accent, #6366f1);
        }

        .log-clear-btn,
        .log-autoscroll-btn {
          background: rgba(0, 0, 0, 0.3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          padding: 4px 8px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.15s;
          color: var(--color-text-secondary);
        }

        .log-clear-btn:hover,
        .log-autoscroll-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: var(--color-text-muted);
        }

        .log-autoscroll-btn.active {
          border-color: rgba(99, 102, 241, 0.4);
          background: rgba(99, 102, 241, 0.1);
        }

        .trade-logs-body {
          max-height: 400px;
          overflow-y: auto;
          padding: 8px 0;
          font-family: var(--font-family-mono);
          font-size: 0.78rem;
          line-height: 1.6;
        }

        .trade-logs-body::-webkit-scrollbar {
          width: 6px;
        }
        .trade-logs-body::-webkit-scrollbar-track {
          background: transparent;
        }
        .trade-logs-body::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }
        .trade-logs-body::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .trade-logs-empty {
          padding: 40px;
          text-align: center;
          color: var(--color-text-muted);
          font-family: var(--font-family-primary, inherit);
          font-size: 0.9rem;
        }

        .log-entry {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 3px 16px;
          transition: background 0.1s;
        }

        .log-entry:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .log-time {
          color: var(--color-text-muted);
          flex-shrink: 0;
          min-width: 70px;
          font-size: 0.72rem;
          padding-top: 1px;
        }

        .log-icon {
          flex-shrink: 0;
          font-size: 0.72rem;
          padding-top: 1px;
        }

        .log-msg {
          word-break: break-word;
          flex: 1;
        }

        /* Subtle left-border accent per level */
        .log-entry.log-entry { border-left: 2px solid transparent; }
        .log-entry.log-success { border-left-color: #22c55e33; }
        .log-entry.log-warning { border-left-color: #facc1533; }
        .log-entry.log-error   { border-left-color: #ef444433; }
        .log-entry.log-entry-level { border-left-color: #818cf833; }
        .log-entry.log-exit    { border-left-color: #f9731633; }
      `}</style>
    </div>
  );
}
